import { ChangeEvent, useCallback, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { postAudioFileAction } from "../redux/actions";
import { getFileExtension } from "../helpers/getFileExtension";
import { ACCEPTABLE_FILE_EXTENSIONS } from "../constants";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectMeetingValue, selectSpeakers } from "../redux/selectors";
import { getUpdatedMeetingValue } from "../helpers/getUpdatedValue";
import { useUpdateStorageValue } from "../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { setMeetingValue } from "../redux/slice";
import { useAppModals } from "../../../app/state/modalSlice/hook/use-app-modals";
import { getArtifactFileAction } from "../../Files/redux/actions";
import { getFileConfig } from "../../Files/helpers";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useFileUpload = () => {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<"upload" | "info">("upload");
  const value = useAppSelector(selectMeetingValue);
  const speakers = useAppSelector(selectSpeakers);
  const { saveValue } = useUpdateStorageValue("meetings", setMeetingValue);
  const { handleOpenModal } = useAppModals();

  const handleFileUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const file = e.target.files?.[0];
      if (!file) return;
      const fileExtension = getFileExtension(file.name) ?? "";

      if (!ACCEPTABLE_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        return;
      }

      if (speakers.length > 0) {
        handleOpenModal("meeting_info_modal", null);
        setFile(file);
        return;
      }

      dispatch(postAudioFileAction(file))
        .unwrap()
        .finally(() => setMode("info"));
      setFile(file);
    },
    [file, mode, speakers.length],
  );

  const handleClick = async (hash: string, fileName: string) => {
    const fileConfig = getFileConfig(fileName);
    if (!fileConfig) return;
    const res = await dispatch(
      getArtifactFileAction({ hash, fileName }),
    ).unwrap();
    const fileExtension = getFileExtension(fileName) ?? "";

    if (!ACCEPTABLE_FILE_EXTENSIONS.includes(fileExtension)) {
      dispatch(
        createNotification(
          "warning",
          `Расширение загруженного файла (${fileExtension}) не поддерживается`,
        ),
      );
      return;
    }

    if (speakers.length > 0) {
      handleOpenModal("meeting_info_modal", null);
      setFile(file);
      return;
    }

    dispatch(postAudioFileAction(res))
      .unwrap()
      .finally(() => setMode("info"));
    setFile(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const fileExtension = getFileExtension(file.name) ?? "";

      if (!ACCEPTABLE_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        return;
      }

      if (speakers.length > 0) {
        handleOpenModal("meeting_info_modal", null);
        return;
      }

      dispatch(postAudioFileAction(file))
        .unwrap()
        .finally(() => setMode("info"));
      setFile(file);
    },
    [file, mode, speakers.length],
  );

  const handleOk = useCallback(() => {
    if (!value?.meetings_object || !file) return;
    const newValue = getUpdatedMeetingValue(value, {
      meeting_info: {
        ...value.meetings_object.meeting_info,
        _user_edited: true,
      },
    });
    saveValue(newValue);
    dispatch(postAudioFileAction(file))
      .unwrap()
      .finally(() => setMode("info"));
  }, [value?.meetings_object, file, mode]);

  const handleCancel = useCallback(() => {
    if (!value?.meetings_object || !file) return;
    const newValue = getUpdatedMeetingValue(value, {
      meeting_info: {
        meeting_name: null,
        meeting_date: null,
        meeting_description: null,
        _user_edited: false,
      },
    });
    saveValue(newValue);
    dispatch(postAudioFileAction(file))
      .unwrap()
      .finally(() => setMode("info"));
  }, [value?.meetings_object, file, mode]);

  return {
    file,
    mode,
    handleFileUpload,
    handleClick,
    handleDrop,
    handleOk,
    handleCancel,
  };
};
